<template>
  <div v-thtml="translation" />
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useCurrencyStore } from '~/stores/useCurrencyStore'
import { formatPrice } from '~/utility/formatCurrency'
import { MoneyType, priceToMoneyType } from '~/utility/money'
import { CampsiteArrivalTax as ArrivalTax } from '../../../campsite-details/types'

interface Props {
  tax: ArrivalTax
}

const props = defineProps<Props>()

const { $t, $i18n } = useNuxtApp()
const currencyStore = useCurrencyStore()

const translation = computed(() => translated())

function getPrice() {
  const originalPrice = priceToMoneyType(props.tax.amount)
  const currentCurrency = currencyStore.currentCurrencyCode
  const formattedOriginalPrice = formatPrice(originalPrice, $i18n.locale)
  if (currentCurrency !== originalPrice.getCurrency()) {
    return getWithOriginalPrice(
      originalPrice,
      currentCurrency,
      formattedOriginalPrice,
    )
  }
  return `<span class="display-price">${formattedOriginalPrice}</span>`
}

function getWithOriginalPrice(
  originalPrice: MoneyType,
  currentCurrency: string,
  formattedOriginalPrice: string,
) {
  const convertedPrice = currencyStore.convert(originalPrice, currentCurrency)
  const formattedConvertedPrice = formatPrice(
    priceToMoneyType(convertedPrice),
    $i18n.locale,
  )
  return `<span class="display-price">${formattedOriginalPrice}</span> <span class="converted-price">(${formattedConvertedPrice})</span>`
}

function arrivalTaxString({ applicable, minAge, maxNights }) {
  const localTaxTranslations = [
    {
      name: 'localTaxNotApplicable',
      condition: !applicable && !minAge && !maxNights,
    },
    {
      name: 'localTaxNotApplicableMinAge',
      condition: !applicable && !!minAge && !maxNights,
    },
    {
      name: 'localTaxNotApplicableMaxNights',
      condition: !applicable && !minAge && !!maxNights,
    },
    {
      name: 'localTaxNotApplicableMinAgeMaxNights',
      condition: !applicable && !!minAge && !!maxNights,
    },
    {
      name: 'localTaxApplicable',
      condition: applicable && !minAge && !maxNights,
    },
    {
      name: 'localTaxApplicableMinAge',
      condition: applicable && !!minAge && !maxNights,
    },
    {
      name: 'localTaxApplicableMaxNights',
      condition: applicable && !minAge && !!maxNights,
    },
    {
      name: 'localTaxApplicableMinAgeMaxNights',
      condition: applicable && !!minAge && !!maxNights,
    },
  ]
  return localTaxTranslations.find((a) => a.condition)!.name
}

function translated() {
  return $t(arrivalTaxString(props.tax), {
    taxText: props.tax.text,
    taxType: props.tax.type,
    minAge: props.tax.minAge,
    price: getPrice(),
    maxNights: props.tax.maxNights,
  })
}
</script>

<style scoped>
.display-price {
  font-weight: bold;
  font-size: 1.125em;
}

.converted-price {
  font-weight: normal;
}
</style>
